/**
 * CustomerAddressBook — the Addresses card on the customer Overview tab.
 *
 * Lifted out of CustomerDetailModal (which was carrying the whole add form
 * inline) when addresses gained editing + tags.
 *
 * Two things it exists to get right:
 *
 *  1. **Removal actually sticks.** The delete button always worked as an array
 *     filter, but syncServiceAddresses() rebuilt the book from linked services
 *     and the legacy single-address field on every open, so the address was
 *     back before the user had finished closing the modal. The service layer
 *     now tombstones removals (Customer.removedAddressKeys) — this component
 *     just has to say plainly, in the confirm copy, that a service-sourced
 *     address stays gone even though the service still references it.
 *
 *  2. **Tagging.** `label` is the address's name ("Nan's place"); `tags[]` is
 *     what kind of place it is (holiday / investment / family / anything they
 *     type). Presets + normalization live in @shared/domain/address-tags.
 *     "Main" is not a tag — it's `isPrimary`, one per customer, shown as the
 *     star badge.
 *
 * The parent owns the themed remove confirmation (one ConfirmModal for the
 * whole customer modal), so removal is raised through `onRequestRemove`.
 */

import { useMemo, useState } from 'react';
import { MapPin, Plus, Home, Star, Trash2, Pencil, X, Check, Tag } from 'lucide-react';
import { Customer, CustomerAddress } from '@/types';
import {
  addCustomerAddress,
  updateCustomerAddress,
  setPrimaryAddress,
} from '@/services/customer.service';
import {
  ADDRESS_TAG_PRESETS,
  MAX_ADDRESS_TAGS,
  addressTagLabel,
  dedupeAddressTags,
  normalizeAddressTag,
} from '@shared/domain/address-tags';
import { AddressTagChips, addressTagClasses } from './AddressTagChips';
import { AddressAutocomplete, type AddressResult } from '@/components/deals/AddressAutocomplete';

/**
 * TODO(saas-readiness): hardcoded Australian state list — should come from a
 * tenant/locale region config so a non-AU tenant doesn't get NSW/VIC/QLD.
 * Moved verbatim from CustomerDetailModal; not silently "fixed" in passing.
 */
const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

const inputClass =
  'w-full mt-1 px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50';

const labelClass = 'text-[10px] font-bold text-white/60 uppercase tracking-wider';

interface AddressDraft {
  label: string;
  address: string;
  addressLine2: string;
  city: string;
  state: string;
  postcode: string;
  tags: string[];
  isPrimary: boolean;
}

function emptyDraft(): AddressDraft {
  return {
    label: '',
    address: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    tags: [],
    isPrimary: false,
  };
}

function draftFrom(addr: CustomerAddress): AddressDraft {
  return {
    label: addr.label || '',
    address: addr.address || '',
    addressLine2: addr.addressLine2 || '',
    city: addr.city || '',
    state: addr.state || '',
    postcode: addr.postcode || '',
    tags: addr.tags || [],
    isPrimary: !!addr.isPrimary,
  };
}

// ---------------------------------------------------------------------------
// Tag picker
// ---------------------------------------------------------------------------

function TagPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
}) {
  const [custom, setCustom] = useState('');
  const selected = new Set(value);
  const atCap = value.length >= MAX_ADDRESS_TAGS;

  const toggle = (slug: string) => {
    onChange(
      selected.has(slug) ? value.filter((t) => t !== slug) : dedupeAddressTags([...value, slug]),
    );
  };

  const addCustom = () => {
    const slug = normalizeAddressTag(custom);
    if (!slug) return;
    onChange(dedupeAddressTags([...value, slug]));
    setCustom('');
  };

  // Tags the user typed themselves — presets are already chips in the row above.
  const customTags = value.filter((t) => !ADDRESS_TAG_PRESETS.some((p) => p.slug === t));

  return (
    <div>
      <label className={labelClass}>
        Tags
        <span className="ml-1.5 normal-case tracking-normal font-medium text-white/35">
          what kind of place is this?
        </span>
      </label>

      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {ADDRESS_TAG_PRESETS.map((preset) => {
          const on = selected.has(preset.slug);
          return (
            <button
              key={preset.slug}
              type="button"
              onClick={() => toggle(preset.slug)}
              disabled={!on && atCap}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                on
                  ? addressTagClasses(preset.slug)
                  : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10 hover:text-white/70'
              }`}
            >
              {on && <Check className="w-3 h-3 inline -mt-px mr-1" />}
              {preset.label}
            </button>
          );
        })}
      </div>

      {customTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {customTags.map((tag) => (
            <span
              key={tag}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full border ${addressTagClasses(tag)}`}
            >
              {addressTagLabel(tag)}
              <button
                type="button"
                onClick={() => onChange(value.filter((t) => t !== tag))}
                className="hover:text-white transition-colors"
                aria-label={`Remove ${addressTagLabel(tag)} tag`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2">
        <input
          type="text"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          disabled={atCap}
          placeholder={atCap ? `Up to ${MAX_ADDRESS_TAGS} tags` : "Or type your own — e.g. Brother's place"}
          className="flex-1 px-3 py-1.5 bg-white/10 border border-white/10 rounded-lg text-white text-xs placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-brand-secondary/50 disabled:opacity-40"
        />
        <button
          type="button"
          onClick={addCustom}
          disabled={atCap || !normalizeAddressTag(custom)}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/85 rounded-lg text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Add tag
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add / edit form
// ---------------------------------------------------------------------------

function AddressForm({
  draft,
  setDraft,
  onSave,
  onCancel,
  saving,
  error,
  mode,
  canSetPrimary,
}: {
  draft: AddressDraft;
  setDraft: (d: AddressDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
  mode: 'add' | 'edit';
  canSetPrimary: boolean;
}) {
  const handleAutocomplete = (r: AddressResult) => {
    setDraft({
      ...draft,
      address: r.fullAddress,
      city: r.city || draft.city,
      state: r.state || draft.state,
      postcode: r.postcode || draft.postcode,
    });
  };

  return (
    <div className="mb-4 p-4 bg-white/5 rounded-lg border border-white/10 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Name this address</label>
          <input
            type="text"
            placeholder="Home, Holiday House, Nan's place…"
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Street Address</label>
          <div className="mt-1">
            <AddressAutocomplete
              value={draft.address}
              onChange={(text) => setDraft({ ...draft, address: text })}
              onAddressSelect={handleAutocomplete}
              placeholder="123 Main St"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>City</label>
          <input
            type="text"
            placeholder="Suburb"
            value={draft.city}
            onChange={(e) => setDraft({ ...draft, city: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>State</label>
          <select
            value={draft.state}
            onChange={(e) => setDraft({ ...draft, state: e.target.value })}
            className={`${inputClass} [&>option]:bg-slate-800`}
          >
            <option value="">Select</option>
            {AU_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Postcode</label>
          <input
            type="text"
            placeholder="2000"
            value={draft.postcode}
            onChange={(e) => setDraft({ ...draft, postcode: e.target.value })}
            className={inputClass}
          />
        </div>
      </div>

      <TagPicker value={draft.tags} onChange={(tags) => setDraft({ ...draft, tags })} />

      {canSetPrimary && (
        <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
          <input
            type="checkbox"
            checked={draft.isPrimary}
            onChange={(e) => setDraft({ ...draft, isPrimary: e.target.checked })}
            className="w-4 h-4 rounded border-white/20 bg-white/10 accent-brand-secondary"
          />
          <span className="text-xs text-white/70">
            Make this the main address
            <span className="text-white/40"> — used by default for new jobs and quotes.</span>
          </span>
        </label>
      )}

      {error && (
        <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={onSave}
          disabled={!draft.address.trim() || saving}
          className="px-4 py-2 bg-brand-secondary hover:brightness-110 text-white font-semibold rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving…' : mode === 'add' ? 'Save Address' : 'Save Changes'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white/85 rounded-lg text-sm transition-colors disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface Props {
  customer: Customer;
  addresses: CustomerAddress[];
  /** Re-read the customer doc after a write — parent owns the addresses state. */
  onChanged: () => Promise<void> | void;
  /** Raised so the parent's themed ConfirmModal handles the destructive step. */
  onRequestRemove: (addr: CustomerAddress) => void;
}

export function CustomerAddressBook({ customer, addresses, onChanged, onRequestRemove }: Props) {
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AddressDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState<string | null>(null);

  // Every tag actually in use, with counts — the filter row. Only earns its
  // space once there's something to narrow down.
  const tagCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const addr of addresses) {
      for (const tag of addr.tags || []) counts.set(tag, (counts.get(tag) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [addresses]);

  const showFilters = addresses.length > 2 && tagCounts.length > 1;

  const visible = useMemo(() => {
    const list = tagFilter ? addresses.filter((a) => (a.tags || []).includes(tagFilter)) : addresses;
    // Main first, then newest-added last so the book reads stably.
    return [...list].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
  }, [addresses, tagFilter]);

  const openAdd = () => {
    setDraft({ ...emptyDraft(), isPrimary: addresses.length === 0 });
    setEditingId(null);
    setError(null);
    setFormMode('add');
  };

  const openEdit = (addr: CustomerAddress) => {
    setDraft(draftFrom(addr));
    setEditingId(addr.id);
    setError(null);
    setFormMode('edit');
  };

  const closeForm = () => {
    setFormMode(null);
    setEditingId(null);
    setError(null);
    setDraft(emptyDraft());
  };

  const handleSave = async () => {
    if (!customer.id || !draft.address.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (formMode === 'add') {
        await addCustomerAddress(customer.id, {
          label: draft.label.trim() || 'Other',
          address: draft.address.trim(),
          addressLine2: draft.addressLine2.trim() || undefined,
          city: draft.city.trim() || undefined,
          state: draft.state || undefined,
          postcode: draft.postcode.trim() || undefined,
          country: customer.country || 'Australia',
          tags: draft.tags,
          // First address in an empty book is the main one whatever the box says.
          isPrimary: draft.isPrimary || addresses.length === 0,
          source: 'manual',
        });
      } else if (editingId) {
        await updateCustomerAddress(customer.id, editingId, {
          label: draft.label.trim() || 'Other',
          address: draft.address.trim(),
          addressLine2: draft.addressLine2.trim() || undefined,
          city: draft.city.trim() || undefined,
          state: draft.state || undefined,
          postcode: draft.postcode.trim() || undefined,
          tags: draft.tags,
          isPrimary: draft.isPrimary,
        });
      }
      await onChanged();
      closeForm();
    } catch (err) {
      console.error('[CustomerAddressBook] Save failed:', err);
      setError(err instanceof Error ? err.message : 'Could not save that address. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (addr: CustomerAddress) => {
    if (!customer.id) return;
    try {
      await setPrimaryAddress(customer.id, addr.id);
      await onChanged();
    } catch (err) {
      console.error('[CustomerAddressBook] Set main failed:', err);
    }
  };

  const hasLegacyOnly = addresses.length === 0 && !!customer.address;

  return (
    <div className="bg-white/5 rounded-lg border border-white/10 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white">
          Addresses
          {addresses.length > 0 && (
            <span className="ml-2 text-sm font-medium text-white/40">{addresses.length}</span>
          )}
        </h3>
        <button
          onClick={() => (formMode === 'add' ? closeForm() : openAdd())}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-secondary hover:brightness-110 text-white font-semibold rounded-lg transition-colors text-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Address
        </button>
      </div>

      {formMode === 'add' && (
        <AddressForm
          draft={draft}
          setDraft={setDraft}
          onSave={handleSave}
          onCancel={closeForm}
          saving={saving}
          error={error}
          mode="add"
          canSetPrimary={addresses.length > 0}
        />
      )}

      {showFilters && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          <Tag className="w-3.5 h-3.5 text-white/30" />
          <button
            onClick={() => setTagFilter(null)}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-colors ${
              tagFilter === null
                ? 'bg-white/15 text-white border-white/25'
                : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10'
            }`}
          >
            All {addresses.length}
          </button>
          {tagCounts.map(([tag, count]) => (
            <button
              key={tag}
              onClick={() => setTagFilter(tagFilter === tag ? null : tag)}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-full border transition-colors ${
                tagFilter === tag
                  ? addressTagClasses(tag)
                  : 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10'
              }`}
            >
              {addressTagLabel(tag)} {count}
            </button>
          ))}
        </div>
      )}

      {addresses.length === 0 && !customer.address ? (
        <div className="text-center py-6 text-white/60">
          <MapPin className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No addresses on file</p>
          <p className="text-xs text-white/40 mt-1">Add an address or link a service to auto-populate</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Legacy single-address field, before the first sync has folded it in. */}
          {hasLegacyOnly && (
            <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg border border-white/10">
              <MapPin className="w-4 h-4 text-brand-secondary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white">{customer.address}</div>
                {customer.addressLine2 && (
                  <div className="text-xs text-white/60">{customer.addressLine2}</div>
                )}
                <div className="text-xs text-white/60">
                  {[customer.city, customer.state, customer.postcode || customer.zipCode]
                    .filter(Boolean)
                    .join(', ')}
                </div>
                <span className="inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/25 font-semibold">
                  Main
                </span>
              </div>
            </div>
          )}

          {visible.length === 0 && tagFilter && (
            <p className="text-sm text-white/50 py-4 text-center">
              No addresses tagged &ldquo;{addressTagLabel(tagFilter)}&rdquo;.
            </p>
          )}

          {visible.map((addr) =>
            formMode === 'edit' && editingId === addr.id ? (
              <AddressForm
                key={addr.id}
                draft={draft}
                setDraft={setDraft}
                onSave={handleSave}
                onCancel={closeForm}
                saving={saving}
                error={error}
                mode="edit"
                canSetPrimary={!addr.isPrimary}
              />
            ) : (
              <div
                key={addr.id}
                className="flex items-start gap-3 p-3 bg-white/5 rounded-lg border border-white/10 group hover:bg-white/10 transition-colors"
              >
                <Home className="w-4 h-4 text-brand-secondary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold text-white">{addr.label}</span>
                    {addr.isPrimary && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/25 font-bold uppercase tracking-wide">
                        <Star className="w-2.5 h-2.5" /> Main
                      </span>
                    )}
                    <AddressTagChips tags={addr.tags} />
                    {addr.source === 'service' && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-medium bg-purple-500/20 text-purple-300 border border-purple-500/25">
                        From Service: {addr.sourceServiceType || 'Service'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white/85 mt-0.5">{addr.address}</div>
                  {addr.addressLine2 && (
                    <div className="text-xs text-white/60">{addr.addressLine2}</div>
                  )}
                  <div className="text-xs text-white/60">
                    {[addr.city, addr.state, addr.postcode].filter(Boolean).join(', ')}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity shrink-0">
                  {!addr.isPrimary && (
                    <button
                      onClick={() => handleSetPrimary(addr)}
                      className="p-1 hover:bg-white/10 rounded text-white/60 hover:text-amber-300"
                      title="Set as main address"
                    >
                      <Star className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(addr)}
                    className="p-1 hover:bg-white/10 rounded text-white/60 hover:text-brand-secondary"
                    title="Edit address & tags"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onRequestRemove(addr)}
                    className="p-1 hover:bg-white/10 rounded text-white/60 hover:text-red-400"
                    title="Remove address"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
